import type { MouseEvent, ReactNode } from 'react';
import { classNames } from './classNames';

export interface Spalte<T> {
  titel: string;
  zelle: (zeile: T) => ReactNode;
  /** Zahlen: Ziffern gleich breit, kein Umbruch. */
  zahl?: boolean;
  /** Auf dem Smartphone über die ganze Kartenbreite (z. B. Namen, Listen). */
  breit?: boolean;
}

/** Mehrfachauswahl: ein Kontrollkästchen je Zeile und eines in der Kopfzeile für alle angezeigten Zeilen. */
export interface TabelleAuswahl<T> {
  gewaehlt: ReadonlySet<string | number>;
  onUmschalten: (zeile: T, an: boolean) => void;
  /** Alle angezeigten Zeilen an- oder abwählen. */
  onAlle: (an: boolean) => void;
  /** Name für Hilfsmittel, z. B. „Max Muster auswählen“. */
  beschriftung: (zeile: T) => string;
}

export interface TabelleProps<T> {
  /** Name der Tabelle für Hilfsmittel. */
  beschriftung: string;
  spalten: Spalte<T>[];
  zeilen: T[];
  schluessel: (zeile: T) => string | number;
  /** Text, wenn es keine Zeilen gibt. */
  leer?: ReactNode;
  /**
   * Klick irgendwo in die Zeile, z. B. zur Detailseite. Nur Mausbequemlichkeit: Für die Tastatur braucht die Zeile
   * trotzdem einen Link. Klicks auf Links, Knöpfe und Felder sowie markierter Text lösen nichts aus.
   */
  onZeile?: (zeile: T) => void;
  /** Mehrfachauswahl; gewählte Zeilen sind in Salbei hinterlegt. */
  auswahl?: TabelleAuswahl<T>;
}

const INTERAKTIV = 'a, button, input, select, textarea, label';

function zeilenKlick<T>(zeile: T, onZeile: (zeile: T) => void) {
  return (e: MouseEvent<HTMLTableRowElement>) => {
    if ((e.target as Element).closest(INTERAKTIV)) return;
    if (window.getSelection()?.toString()) return;
    onZeile(zeile);
  };
}

/**
 * Einfache Tabelle für die Verwaltung im Aussehen der Vorgangsliste aus Axis: weiße Karte, jede Zeile ein
 * Linde-Balken. Unter 640 px wird jede Zeile zur Karte, jede Zelle zeigt dann ihre Spaltenüberschrift
 * (`data-label`). Ohne Sortierung – die Listen sind klein und werden über Filter eingegrenzt. Eine Mehrfachauswahl gibt es
 * nur auf Wunsch (`auswahl`).
 */
export function Tabelle<T>({
  beschriftung,
  spalten,
  zeilen,
  schluessel,
  leer,
  onZeile,
  auswahl,
}: TabelleProps<T>) {
  const alleAn =
    auswahl !== undefined &&
    zeilen.length > 0 &&
    zeilen.every((z) => auswahl.gewaehlt.has(schluessel(z)));
  const teilsAn =
    auswahl !== undefined && !alleAn && zeilen.some((z) => auswahl.gewaehlt.has(schluessel(z)));
  return (
    <div className="ksb-tablewrap">
      <table className="ksb-table" aria-label={beschriftung}>
        <thead>
          <tr>
            {auswahl && (
              <th scope="col" className="ksb-table__wahl">
                <input
                  type="checkbox"
                  className="ksb-cb"
                  aria-label="Alle angezeigten auswählen"
                  checked={alleAn}
                  disabled={zeilen.length === 0}
                  ref={(el) => {
                    if (el) el.indeterminate = teilsAn;
                  }}
                  onChange={(e) => auswahl.onAlle(e.target.checked)}
                />
              </th>
            )}
            {spalten.map((s) => (
              <th key={s.titel} scope="col">
                {s.titel}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z) => (
            <tr
              key={schluessel(z)}
              className={classNames(
                onZeile && 'ksb-table__klick',
                auswahl?.gewaehlt.has(schluessel(z)) && 'ksb-table__gewaehlt',
              )}
              {...(onZeile && { onClick: zeilenKlick(z, onZeile) })}
            >
              {auswahl && (
                <td className="ksb-table__wahl">
                  <input
                    type="checkbox"
                    className="ksb-cb"
                    aria-label={auswahl.beschriftung(z)}
                    checked={auswahl.gewaehlt.has(schluessel(z))}
                    onChange={(e) => auswahl.onUmschalten(z, e.target.checked)}
                  />
                </td>
              )}
              {spalten.map((s) => (
                <td
                  key={s.titel}
                  data-label={s.titel}
                  className={classNames(s.zahl && 'ksb-table__num', s.breit && 'ksb-table__breit')}
                >
                  {s.zelle(z)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {zeilen.length === 0 && leer !== undefined && <p className="ksb-table__leer">{leer}</p>}
    </div>
  );
}
